import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../hooks/useAltHotkeys';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { Search, Users, Loader, AlertCircle } from 'lucide-react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { EnrichedDriver } from './DriverDrawer';
import { DriverCard } from './DriverCard';
import { createTruckMarkerIcon } from '../utils/truckMarkerUtils';
import { getGoogleMapsLoaderOptions } from '../config/googleMaps';
import MultiSelect from './MultiSelect';
import { Drawer } from './ui/Drawer';

// ============================================================================
// Types
// ============================================================================

/** A stop/waypoint on the route */
export interface RouteStop {
  name: string;
  cityState?: string;
  coordinates: string; // "lat,lng" format
  type: 'origin' | 'stop' | 'destination';
  legnum?: number;
}

/** Props for the RouteMapModal component */
export interface RouteMapModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal is closed */
  onClose: () => void;

  // Route information
  /** Array of stops defining the route (minimum 2: origin and destination) */
  stops: RouteStop[];

  // Order context (optional)
  /** PRO number for display */
  pronumber?: number;
  /** Leg number for display */
  legnum?: number;
  /** Customer name for display */
  customerName?: string;

  // Driver selection (optional - omit to show route-only view)
  /** Array of drivers to display on map and in list */
  drivers?: EnrichedDriver[];
  /** Callback when a driver is selected/assigned */
  onSelectDriver?: (driver: EnrichedDriver) => void;
  /** Whether drivers are loading */
  driversLoading?: boolean;
  /** Error message for driver loading */
  driversError?: string | null;

  // Display options
  /** Display mode: 'modal' for centered modal, 'drawer' for right-side drawer */
  mode?: 'modal' | 'drawer';
  /** Custom title for the header */
  title?: string;
}

// ============================================================================
// Constants
// ============================================================================

// Marker colors
const COLORS = {
  origin: '#10B981',      // Green
  stop: '#F59E0B',        // Orange/Amber
  destination: '#EF4444', // Red
  route: '#9333EA',       // Purple
};

// ============================================================================
// Component
// ============================================================================

export const RouteMapModal: React.FC<RouteMapModalProps> = ({
  open,
  onClose,
  stops,
  pronumber,
  legnum,
  customerName,
  drivers = [],
  onSelectDriver,
  driversLoading = false,
  driversError = null,
  mode = 'modal',
  title,
}) => {
  // State
  const [selectedDriver, setSelectedDriver] = useState<EnrichedDriver | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [selectedDispatchers, setSelectedDispatchers] = useState<string[]>([]);
  const [mapType, setMapType] = useState<'roadmap' | 'hybrid'>('roadmap');

  // Refs
  const mapRef = useRef<google.maps.Map | null>(null);
  const driverMarkersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const stopMarkersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const routeRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);

  // Determine if we're showing drivers
  const showDrivers = drivers.length > 0 || onSelectDriver !== undefined;

  // Load Google Maps
  const { isLoaded: isMapLoaded } = useJsApiLoader(getGoogleMapsLoaderOptions());

  // ============================================================================
  // Coordinate Parsing
  // ============================================================================

  const parseCoordinates = useCallback((coordString: string | null): google.maps.LatLngLiteral | null => {
    if (!coordString) return null;

    let cleaned = coordString.replace(/\r/g, '').trim();

    // Handle polygon/multi-point strings - take first point
    if (cleaned.includes(';')) {
      cleaned = cleaned.split(';')[0].trim();
    } else if (cleaned.includes('\n')) {
      cleaned = cleaned.split('\n')[0].trim();
    }

    const parts = cleaned.split(',').map(s => s.trim());
    if (parts.length !== 2) return null;

    const [first, second] = parts.map(parseFloat);
    if (isNaN(first) || isNaN(second)) return null;

    // Check if coordinates are valid lat/lng ranges
    const isFirstLat = first >= -90 && first <= 90;
    const isSecondLng = second >= -180 && second <= 180;

    if (isFirstLat && isSecondLng) {
      return { lat: first, lng: second };
    }

    // Try reversed order (lng,lat)
    const isFirstLng = first >= -180 && first <= 180;
    const isSecondLat = second >= -90 && second <= 90;

    if (isFirstLng && isSecondLat) {
      return { lat: second, lng: first };
    }

    return null;
  }, []);

  // ============================================================================
  // Cleanup Functions
  // ============================================================================

  const clearRoute = useCallback(() => {
    if (routeRendererRef.current) {
      routeRendererRef.current.setMap(null);
      routeRendererRef.current = null;
    }
  }, []);

  const clearStopMarkers = useCallback(() => {
    stopMarkersRef.current.forEach(marker => {
      marker.map = null;
    });
    stopMarkersRef.current = [];
  }, []);

  const clearDriverMarkers = useCallback(() => {
    driverMarkersRef.current.forEach(marker => {
      marker.map = null;
    });
    driverMarkersRef.current = [];
  }, []);

  // ============================================================================
  // Derived Data
  // ============================================================================

  // Parse all stop coordinates
  const parsedStops = useMemo(() => {
    return stops.map(stop => ({
      ...stop,
      coords: parseCoordinates(stop.coordinates),
    }));
  }, [stops, parseCoordinates]);

  // Get origin and destination
  const origin = useMemo(() => parsedStops.find(s => s.type === 'origin'), [parsedStops]);
  const destination = useMemo(() => parsedStops.find(s => s.type === 'destination'), [parsedStops]);
  const waypoints = useMemo(() => parsedStops.filter(s => s.type === 'stop'), [parsedStops]);

  // Calculate initial map center based on origin (avoid defaulting to SF)
  const initialCenter = useMemo(() => {
    if (origin?.coords) return origin.coords;
    if (destination?.coords) return destination.coords;
    // Fallback to center of US if no coordinates
    return { lat: 39.8283, lng: -98.5795 };
  }, [origin, destination]);


  // Get unique dispatchers from drivers
  const dispatcherOptions = useMemo(() => {
    const dispatchers = new Set<string>();
    drivers.forEach(driver => {
      if (driver.dispatcher) {
        dispatchers.add(driver.dispatcher);
      }
    });
    return Array.from(dispatchers)
      .sort()
      .map(dispatcher => ({
        value: dispatcher,
        label: dispatcher,
      }));
  }, [drivers]);

  // Filter drivers
  const filteredDrivers = useMemo(() => {
    let filtered = drivers;

    if (filterText) {
      const searchLower = filterText.toLowerCase();
      filtered = filtered.filter(driver =>
        driver.unitNumber?.toLowerCase().includes(searchLower) ||
        driver.driverName?.toLowerCase().includes(searchLower)
      );
    }

    if (selectedDispatchers.length > 0) {
      filtered = filtered.filter(driver =>
        driver.dispatcher && selectedDispatchers.includes(driver.dispatcher)
      );
    }

    return filtered;
  }, [drivers, filterText, selectedDispatchers]);

  // Qualified count
  const qualifiedCount = useMemo(() => {
    return filteredDrivers.filter(d => d.isQualified).length;
  }, [filteredDrivers]);

  // Sorted drivers with selected at top
  const sortedDrivers = useMemo(() => {
    if (!selectedDriver) return filteredDrivers;

    const selected = filteredDrivers.find(d => d.unitNumber === selectedDriver.unitNumber);
    if (!selected) return filteredDrivers;

    const others = filteredDrivers.filter(d => d.unitNumber !== selectedDriver.unitNumber);
    return [selected, ...others];
  }, [filteredDrivers, selectedDriver]);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleDriverClick = useCallback((driver: EnrichedDriver) => {
    setSelectedDriver(driver);

    if (driver.location && mapRef.current) {
      mapRef.current.panTo({
        lat: driver.location.latitude,
        lng: driver.location.longitude,
      });
      mapRef.current.setZoom(12);
    }
  }, []);

  const handleAssignDriver = useCallback(() => {
    if (selectedDriver && onSelectDriver) {
      onSelectDriver(selectedDriver);
    }
  }, [selectedDriver, onSelectDriver]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMapReady(true);
  }, []);

  // ============================================================================
  // Effects
  // ============================================================================

  // Update map type
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setMapTypeId(mapType);
  }, [mapType]);

  // Store route bounds to fit after drawer animation completes
  const pendingBoundsRef = useRef<google.maps.LatLngBounds | null>(null);

  // Track when drawer animation is complete (500ms transition)
  const [drawerAnimationComplete, setDrawerAnimationComplete] = useState(false);

  useEffect(() => {
    if (open) {
      // Wait for drawer slide-in animation to complete before fitting bounds
      const timer = setTimeout(() => setDrawerAnimationComplete(true), 550);
      return () => clearTimeout(timer);
    } else {
      setDrawerAnimationComplete(false);
      pendingBoundsRef.current = null;
    }
  }, [open]);

  // Fit bounds once drawer animation completes
  useEffect(() => {
    if (drawerAnimationComplete && pendingBoundsRef.current && mapRef.current) {
      mapRef.current.fitBounds(pendingBoundsRef.current, { top: 50, right: 50, bottom: 50, left: 50 });
      pendingBoundsRef.current = null;
    }
  }, [drawerAnimationComplete]);

  // Draw route and stop markers
  useEffect(() => {
    if (!mapReady || !mapRef.current || !open) {
      clearRoute();
      clearStopMarkers();
      return;
    }

    if (!origin?.coords || !destination?.coords) {
      clearRoute();
      clearStopMarkers();
      return;
    }

    clearRoute();
    clearStopMarkers();

    // Create stop markers
    if (window.google?.maps?.marker?.PinElement) {
      parsedStops.forEach((stop, index) => {
        if (!stop.coords) return;

        const color = COLORS[stop.type];
        const pin = new google.maps.marker.PinElement({
          background: color,
          borderColor: '#ffffff',
          glyphColor: '#ffffff',
          scale: stop.type === 'stop' ? 0.9 : 1.0,
        });

        const marker = new google.maps.marker.AdvancedMarkerElement({
          position: stop.coords,
          map: mapRef.current,
          content: pin.element,
          title: stop.name || `Stop ${index + 1}`,
          zIndex: stop.type === 'origin' ? 10002 : stop.type === 'destination' ? 10001 : 10000,
        });

        stopMarkersRef.current.push(marker);
      });
    }

    // Draw route with waypoints
    const directionsService = new google.maps.DirectionsService();

    const waypointsForRoute: google.maps.DirectionsWaypoint[] = waypoints
      .filter(wp => wp.coords)
      .map(wp => ({
        location: wp.coords!,
        stopover: true,
      }));

    directionsService.route(
      {
        origin: origin.coords,
        destination: destination.coords,
        waypoints: waypointsForRoute,
        travelMode: google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false, // Keep order as specified
      },
      (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result) {
          routeRendererRef.current = new google.maps.DirectionsRenderer({
            map: mapRef.current,
            suppressMarkers: true, // We draw our own markers
            polylineOptions: {
              strokeColor: COLORS.route,
              strokeWeight: 4,
              strokeOpacity: 0.8,
            },
          });
          routeRendererRef.current.setDirections(result);

          // Fit bounds to show entire route - wait for drawer animation to avoid bounce
          if (result.routes[0]?.bounds) {
            if (drawerAnimationComplete) {
              // Animation already done, fit immediately
              mapRef.current?.fitBounds(result.routes[0].bounds, { top: 50, right: 50, bottom: 50, left: 50 });
            } else {
              // Store bounds to fit after animation completes
              pendingBoundsRef.current = result.routes[0].bounds;
            }
          }
        }
      }
    );

    return () => {
      clearRoute();
      clearStopMarkers();
    };
  }, [mapReady, open, parsedStops, origin, destination, waypoints, clearRoute, clearStopMarkers, drawerAnimationComplete]);

  // Draw driver markers
  useEffect(() => {
    if (!mapReady || !mapRef.current || !open || !showDrivers) {
      clearDriverMarkers();
      return;
    }

    clearDriverMarkers();

    const driversToShow = filteredDrivers.filter(driver => driver.location && driver.isQualified);

    if (window.google?.maps?.marker?.AdvancedMarkerElement) {
      driversToShow.forEach(driver => {
        const position = {
          lat: driver.location!.latitude,
          lng: driver.location!.longitude,
        };

        const isSelected = selectedDriver?.unitNumber === driver.unitNumber;

        const truckIcon = createTruckMarkerIcon({
          speed: driver.location?.speed || 0,
          heading: undefined,
          hasDispatch: !!driver.dispatchInfo,
          isCompleted: driver.dispatchInfo?.isCompleted ?? true,
          remainingDispatches: 0,
          hasOriginArrivalTime: false,
          truckType: 'TruckWithContainer',
          sizeMultiplier: 1.2,
          unitNumber: driver.unitNumber,
          isSelected,
          isDarkMode: false,
          opacity: 1,
          isOOS: false,
        });

        const marker = new google.maps.marker.AdvancedMarkerElement({
          position,
          map: mapRef.current!,
          content: truckIcon,
          title: `${driver.unitNumber} - ${driver.driverName}`,
        });

        marker.addListener('click', () => {
          setSelectedDriver(driver);
          if (mapRef.current) {
            mapRef.current.panTo(position);
            mapRef.current.setZoom(12);
          }
        });

        driverMarkersRef.current.push(marker);
      });
    }

    return () => {
      clearDriverMarkers();
    };
  }, [mapReady, open, showDrivers, filteredDrivers, selectedDriver, clearDriverMarkers]);

  // Cleanup on close
  useEffect(() => {
    if (!open) {
      clearDriverMarkers();
      clearStopMarkers();
      clearRoute();
      setSelectedDriver(null);
      setMapReady(false);
      setFilterText('');
      setSelectedDispatchers([]);
      setMapType('roadmap');
    }
  }, [open, clearDriverMarkers, clearStopMarkers, clearRoute]);

  // ============================================================================
  // Render
  // ============================================================================

  // Read-only route map: Alt+C closes; the layer keeps Alt+S off the surface below.
  useAltHotkeys({ C: onClose }, undefined, { layer: open });

  if (!open) return null;

  const modalTitle = title || (pronumber ? `Route: PRO #${pronumber}${legnum ? `-${legnum}` : ''}` : 'Route Map');

  // Map content - shared between modal and drawer modes
  const mapContent = (
    <div className={`flex flex-col ${mode === 'drawer' ? 'h-full' : 'flex-1'}`}>
      {/* Route Summary */}
      {(customerName || stops.length > 0) && (
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <div className="space-y-2">
            {customerName && (
              <div>
                <span className="text-xs text-muted">Customer: </span>
                <span className="text-sm font-medium text-heading">{customerName}</span>
              </div>
            )}
            {stops.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                {stops.map((stop, idx) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && <span className="text-gray-400">→</span>}
                    <span className={`text-sm ${
                      stop.type === 'origin' ? 'text-green-600 dark:text-green-400' :
                      stop.type === 'destination' ? 'text-red-600 dark:text-red-400' :
                      'text-orange-600 dark:text-orange-400'
                    }`}>
                      {stop.name}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Map Container */}
      <div className={`flex-1 relative ${mode === 'drawer' ? 'min-h-[400px]' : ''}`}>
        {isMapLoaded ? (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={initialCenter}
            zoom={6}
            onLoad={onMapLoad}
            options={{
              mapId: '3cf64d0fcebdaf11f3625d6e',
              disableDefaultUI: false,
              zoomControl: true,
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: true,
              clickableIcons: false,
            }}
          />
        ) : (
          <div className="flex items-center justify-center h-full min-h-[300px]">
            <Loader className="h-8 w-8 text-blue-600 animate-spin" />
          </div>
        )}

        {/* Satellite Toggle */}
        {isMapLoaded && (
          <button
            onClick={() => setMapType(prev => prev === 'roadmap' ? 'hybrid' : 'roadmap')}
            className="absolute top-3 left-3 bg-panel hover:bg-gray-100 dark:hover:bg-gray-700 rounded shadow-md p-2 z-10"
            title={mapType === 'roadmap' ? 'Switch to Satellite view' : 'Switch to Map view'}
          >
            {mapType === 'roadmap' ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6 text-gray-500 dark:text-gray-300">
                <circle cx="12" cy="12" r="10" />
                <path d="M2 12h20" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6 text-gray-500 dark:text-gray-300">
                <path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3V6z" />
                <path d="M9 3v15" />
                <path d="M15 6v15" />
              </svg>
            )}
          </button>
        )}

        {/* Legend */}
        {isMapLoaded && (
          <div className="absolute bottom-3 left-3 bg-panel rounded shadow-md p-2 z-10 text-xs">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.origin }} />
                <span className="text-gray-600 dark:text-gray-300">Origin</span>
              </div>
              {waypoints.length > 0 && (
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.stop }} />
                  <span className="text-gray-600 dark:text-gray-300">Stop</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.destination }} />
                <span className="text-gray-600 dark:text-gray-300">Destination</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Driver List (when drivers provided) */}
      {showDrivers && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          {/* Search/Filter */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Filter by name or unit number..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-heading placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {dispatcherOptions.length > 0 && (
              <div className="mt-3">
                <MultiSelect
                  options={dispatcherOptions}
                  value={selectedDispatchers}
                  onChange={setSelectedDispatchers}
                  placeholder="Filter by dispatcher..."
                  showClearOption={true}
                  clearOptionLabel="All Dispatchers"
                  enableSearch={true}
                  searchPlaceholder="Search dispatchers..."
                  countThreshold={2}
                />
              </div>
            )}

            {!driversLoading && (
              <div className="mt-2 flex items-center justify-between text-xs text-muted">
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {filteredDrivers.length} drivers available
                </span>
                {qualifiedCount > 0 && (
                  <span className="text-green-600 dark:text-green-400">
                    {qualifiedCount} qualified
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Driver List */}
          <div className="max-h-[250px] overflow-y-auto px-4 py-3">
            {driversLoading && (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader className="h-6 w-6 text-blue-600 animate-spin mb-2" />
                <p className="text-sm text-gray-600 dark:text-gray-400">Loading nearby drivers...</p>
              </div>
            )}

            {!driversLoading && driversError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700 dark:text-red-300">{driversError}</p>
                </div>
              </div>
            )}

            {!driversLoading && !driversError && filteredDrivers.length === 0 && (
              <div className="text-center py-6">
                <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {filterText || selectedDispatchers.length > 0 ? 'No drivers match your search' : 'No nearby drivers found'}
                </p>
              </div>
            )}

            {!driversLoading && !driversError && filteredDrivers.filter(d => d.isQualified).length > 0 && (
              <div className="space-y-2">
                {sortedDrivers
                  .filter(d => d.isQualified)
                  .map((driver, index) => (
                    <DriverCard
                      key={`qualified-${driver.unitNumber}-${index}`}
                      driver={driver}
                      onClick={() => handleDriverClick(driver)}
                      index={index}
                      isSelected={selectedDriver?.unitNumber === driver.unitNumber}
                    />
                  ))}
              </div>
            )}
          </div>

          {/* Assign Button (when driver is selected) */}
          {selectedDriver && onSelectDriver && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-panel">
              <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <p className="text-sm font-medium text-heading">
                  {selectedDriver.unitNumber} - {selectedDriver.driverName}
                </p>
                {selectedDriver.distanceFromOrigin !== null && (
                  <p className="text-xs text-muted mt-1">
                    {selectedDriver.distanceFromOrigin.toFixed(1)} miles from origin
                  </p>
                )}
              </div>
              <button
                onClick={handleAssignDriver}
                className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Users className="h-4 w-4" />
                Assign Driver
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Drawer mode
  if (mode === 'drawer') {
    return (
      <Drawer
        isOpen={open}
        onClose={onClose}
        title={modalTitle}
        position="right"
        maxWidth="xl"
        noBackdrop={true}
      >
        {mapContent}
      </Drawer>
    );
  }

  // Modal mode (default)
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-scrim/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-panel rounded-lg shadow-2xl h-[80vh] w-[70vw] max-w-4xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-900">
          <h2 className="text-lg font-semibold text-heading">{modalTitle}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
            title="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {mapContent}
      </div>
    </div>,
    document.body
  );
};

export default RouteMapModal;
